// pylee/apps/web/app/page.tsx
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import CTA from '@/app/(home)/components/cta';
import Features from '@/app/(home)/components/features';
import Hero from '@/app/(home)/components/hero';
import HowItWorks from '@/app/(home)/components/how-it-works';
import RealWorldImpact from '@/app/(home)/components/use-cases';
import FromDayOne from '@/app/(home)/components/from-day-one';
import ValueProp from '@/app/(home)/components/value-prop';
import Compatibility from '@/app/(home)/components/compatibility'; 
import CTABanner from '@/app/(home)/components/cta-middle';
import CTAInline from '@/app/(home)/components/cta-inline';

// Constants
const CALENDLY_LINK = 'https://calendly.com/bucephalus/pylee-demo';

// Helpers
function lerp(start: number, end: number, amount: number): number {
  return start + amount * (end - start);
}
const debounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<F>): void => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), waitFor);
  };
};

// --- Easing function (Cubic Ease-Out) ---
// Input 't' is progress from 0 to 1. Output is eased value from 0 to 1.
// For opacity fade (1 to 0), we calculate 1 - easedFadeProgress
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}


export default function HomePage() {
  // State
  const [isFixed, setIsFixed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Refs
  const heroWrapperRef = useRef<HTMLDivElement>(null);
  const heroTextContentRef = useRef<HTMLDivElement>(null);

  // Config constants
  const initialNavBgAlpha = 0.7;
  const scrolledNavBgAlpha = 0.85;
  const initialNavBlur = 5;
  const scrolledNavBlur = 12;
  // --- Adjust Fade Thresholds ---
  const fadeStartThreshold = 0.15; // Start fading much sooner (e.g., at 15% scroll)
  const fadeEndThreshold = 0.7;   // Finish fading sooner (e.g., by 70% scroll)
  const visualizationOffset = 150;

  // --- Effect: Handle Resize ---
  const handleResize = useCallback(() => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    // Reset text opacity immediately on resize if needed
    const textContentEl = heroTextContentRef.current;
    if (textContentEl && mobile) {
       textContentEl.style.opacity = '1';
       textContentEl.style.pointerEvents = 'auto';
    } else if (textContentEl && !mobile && !isFixed) {
       textContentEl.style.opacity = '1';
       textContentEl.style.pointerEvents = 'auto';
    }
  }, [isFixed]);

  useEffect(() => {
    const debouncedResizeHandler = debounce(handleResize, 150);
    handleResize();
    window.addEventListener('resize', debouncedResizeHandler);
    return () => window.removeEventListener('resize', debouncedResizeHandler);
  }, [handleResize]);

  // --- Callback: Handle Scroll ---
  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    const docElement = document.documentElement;
    const headerElement = document.getElementById('main-navbar');
    const headerHeight = headerElement?.offsetHeight ?? 80;
    const wrapperEl = heroWrapperRef.current;
    const textContentEl = heroTextContentRef.current;

    if (!wrapperEl || !textContentEl) return;

    const heroWrapperTop = wrapperEl.offsetTop ?? 0;
    const heroWrapperHeight = wrapperEl.offsetHeight ?? 0;
    const heroTextContentHeight = textContentEl.offsetHeight ?? 0;

    docElement.style.setProperty('--fixed-content-top', `${headerHeight + 20}px`);

    // Mobile Handling (remains the same)
    if (isMobile) {
      // ... (keep mobile reset logic) ...
       if (isFixed) setIsFixed(false);
      textContentEl.style.opacity = '1';
      textContentEl.style.pointerEvents = 'auto';
      docElement.style.setProperty('--gradient-overlay-opacity', scrollY >= heroWrapperTop ? '1' : '0');
      docElement.style.setProperty('--navbar-bg-alpha', String(initialNavBgAlpha));
      docElement.style.setProperty('--navbar-blur', `${initialNavBlur}px`);
      return;
    }

    // Desktop Scroll Logic
    const scrollBuffer = 100;
    const heroScrollEnd = heroWrapperTop + heroWrapperHeight - headerHeight - heroTextContentHeight - scrollBuffer - visualizationOffset;
    const effectiveHeroScrollEnd = Math.max(heroWrapperTop + 150, heroScrollEnd);
    const heroScrollStart = heroWrapperTop;
    const heroScrollDistance = Math.max(1, effectiveHeroScrollEnd - heroScrollStart);
    const heroScrollProgress = Math.max(0, Math.min(1, (scrollY - heroScrollStart) / heroScrollDistance));

    const shouldBeFixed = scrollY >= heroScrollStart && scrollY < effectiveHeroScrollEnd;
    const isAfterHero = scrollY >= effectiveHeroScrollEnd;

    if (shouldBeFixed !== isFixed) setIsFixed(shouldBeFixed);

    // --- Calculate GRADUAL Fade Opacity with CUBIC Easing ---
    let currentHeroTextOpacity = 1;
    if (shouldBeFixed) {
      const fadeDuration = Math.max(0.01, fadeEndThreshold - fadeStartThreshold);
      // Calculate the raw progress *within* the fade duration (0 to 1)
      const rawFadeProgress = Math.max(0, Math.min(1, (heroScrollProgress - fadeStartThreshold) / fadeDuration));

      // Apply the easing function to the fade progress
      const easedFadeProgress = easeOutCubic(rawFadeProgress); // Use the cubic ease-out function

      // Calculate opacity (fading from 1 down to 0)
      currentHeroTextOpacity = 1 - easedFadeProgress;

    } else if (isAfterHero) {
      currentHeroTextOpacity = 0;
    }
    // Apply opacity and pointer events
    textContentEl.style.opacity = currentHeroTextOpacity.toFixed(2);
    textContentEl.style.pointerEvents = currentHeroTextOpacity < 0.1 ? 'none' : 'auto';

    // Update Global CSS Variables for Nav/Overlay (remains the same)
    if (scrollY < heroScrollStart) {
        // ... (reset styles before fixed) ...
        docElement.style.setProperty('--gradient-overlay-opacity', '0');
        docElement.style.setProperty('--navbar-bg-alpha', String(initialNavBgAlpha));
        docElement.style.setProperty('--navbar-blur', `${initialNavBlur}px`);
        if (!shouldBeFixed) {
            textContentEl.style.opacity = '1';
            textContentEl.style.pointerEvents = 'auto';
        }
    } else {
        // ... (lerp styles during fixed scroll) ...
        const lerpProgress = Math.min(1, heroScrollProgress);
        const currentOverlayOpacity = Math.min(1, lerpProgress * 1.2);
        const currentNavBgAlpha = lerp(initialNavBgAlpha, scrolledNavBgAlpha, lerpProgress);
        const currentNavBlur = lerp(initialNavBlur, scrolledNavBlur, lerpProgress);

        docElement.style.setProperty('--gradient-overlay-opacity', currentOverlayOpacity.toFixed(2));
        docElement.style.setProperty('--navbar-bg-alpha', currentNavBgAlpha.toFixed(2));
        docElement.style.setProperty('--navbar-blur', `${currentNavBlur.toFixed(1)}px`);

        if (isAfterHero) {
            // ... (set final styles after fixed scroll) ...
            docElement.style.setProperty('--gradient-overlay-opacity', '1');
            docElement.style.setProperty('--navbar-bg-alpha', String(scrolledNavBgAlpha));
            docElement.style.setProperty('--navbar-blur', `${scrolledNavBlur}px`);
            textContentEl.style.opacity = '0';
            textContentEl.style.pointerEvents = 'none';
        }
    }
  }, [isFixed, isMobile]); // Dependencies updated

  // --- Effect: Add/Remove Listeners ---
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    const initialCalcTimeout = setTimeout(handleScroll, 100);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(initialCalcTimeout);
    };
  }, [handleScroll]);

  // --- Effect: Smooth Scroll ---
  useEffect(() => {
    // ... (smooth scroll logic remains the same) ...
    const smoothScrollHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href^="#"]') as HTMLAnchorElement | null;
      if (link && link.hash) {
        const targetId = link.hash;
        const targetElement = document.querySelector(targetId);
        if (targetElement) {
          e.preventDefault();
          const headerElement = document.getElementById('main-navbar');
          const headerHeight = headerElement?.offsetHeight ?? 80;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight - 30;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }
    };
    document.addEventListener('click', smoothScrollHandler);
    return () => {
      document.removeEventListener('click', smoothScrollHandler);
    };
  }, []);


  return (
    <>
      <div ref={heroWrapperRef}>
        <Hero
          ref={heroTextContentRef}
          isFixed={isFixed}
          calendlyLink={CALENDLY_LINK}
        />
      </div>
      <Compatibility />
      <CTAInline 
        text="It's easy to get started. Just make an account."
        primaryCampaign="compatability_cta"
        fullWidthBanner={true}
      />      
      <HowItWorks />
      <Features />
      <CTAInline 
        text="Ready to experience these features for your team?"
        primaryCampaign="features_cta"
        fullWidthBanner={true}
      />
      
      <ValueProp />
      
      <RealWorldImpact />
      <CTAInline 
        text="Start simplyfing your workflows today."
        primaryCampaign="impact_cta"
        fullWidthBanner={true}
      />
      <FromDayOne />
      <CTA
        calendlyLink={CALENDLY_LINK}
      />
    </>
  );
}